import { Instagram } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useConsentStore } from '@/features/consent/store/consentStore'
import { CreateSessionLink } from '@/features/sessions/components/CreateSessionLink'
import { isNative } from '@/shared/api/platform'
import { AppStoreBadges } from '@/shared/components/AppStoreBadges'
import { useLocalizedHref } from '@/shared/hooks/useLocalizedHref'
import { INSTAGRAM_URL } from '@/shared/seo/siteIdentity'

// min-h-6 = 24px de área táctil (WCAG 2.2 AA), sin cambiar el tamaño del texto.
const LINK =
  'inline-flex min-h-6 items-center text-background/75 transition-colors hover:text-yellow'

/**
 * Pie global (lo monta MainLayout en las rutas públicas).
 *
 * Dos bloques y una línea:
 *  - Marca: logo, lema, insignias de las apps (compactas, en fila) e Instagram.
 *  - Navegación: dos columnas cortas (Producto · Match&Play).
 *  - Línea legal: © + claim a un lado; Términos · Privacidad · Gestionar cookies al otro. Lo legal va
 *    aquí y no en una tercera columna: eran dos enlaces sueltos que desequilibraban la rejilla.
 *
 * En móvil la MobileTabBar (sticky) va DESPUÉS del pie en el flujo: al final de la página
 * ocupa su propio sitio y no tapa nada, así que no hace falta reservarle hueco.
 */
export function SiteFooter() {
  const { t } = useTranslation()
  const year = new Date().getFullYear()
  const reopenConsent = useConsentStore((s) => s.reopenBanner)
  const aboutHref = useLocalizedHref('about')
  const helpHref = useLocalizedHref('help-hub')
  const termsHref = useLocalizedHref('terms')
  const privacyHref = useLocalizedHref('privacy')

  return (
    <footer className="border-t-4 border-foreground bg-foreground text-background">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-10 md:grid-cols-[minmax(0,1fr)_auto] md:gap-16 md:px-6 md:py-14">
        {/* Marca */}
        <div className="max-w-sm">
          <img
            src="/brand/logo-lockup-light.png"
            alt="Match&Play"
            width={1500}
            height={478}
            draggable={false}
            className="h-9 w-auto"
          />
          <p className="mt-3 text-sm text-background/70">{t('landing.footer.tagline')}</p>
          <div className="mt-5 flex flex-wrap items-stretch gap-2">
            <AppStoreBadges placement="footer" variant="dark" size="sm" />
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('landing.footer.instagramAria')}
              // Misma altura que las insignias (items-stretch) y cuadrado.
              className="brutal-sm brutal-press inline-flex aspect-square min-h-11 items-center justify-center rounded-lg border-2 border-background bg-background text-foreground"
            >
              <Instagram size={20} strokeWidth={2.25} aria-hidden="true" />
            </a>
          </div>
        </div>

        {/* Navegación */}
        <nav
          aria-label={t('landing.footer.navAria')}
          className="grid grid-cols-2 gap-x-12 gap-y-8 sm:gap-x-20"
        >
          <FooterColumn
            title={t('landing.footer.product.title')}
            links={[
              { to: '/sessions', label: t('landing.footer.product.explore') },
              { to: '/locales', label: t('landing.footer.product.stores') },
              { to: '/sessions/new', label: t('landing.footer.product.create') },
            ]}
          />
          <FooterColumn
            title={t('landing.footer.brandTitle')}
            links={[
              { to: aboutHref, label: t('landing.footer.company.about') },
              { to: helpHref, label: t('landing.footer.company.help') },
              { to: '/contact', label: t('landing.footer.company.contact') },
            ]}
          />
        </nav>
      </div>

      {/* Línea legal. «Gestionar cookies» reabre el banner aunque ya se haya decidido (RGPD: revocar
          debe ser fácil). Solo en web: en nativo no hay cookies de tracking ni banner. */}
      <div className="border-t border-background/15">
        <div className="mx-auto flex max-w-7xl flex-col-reverse gap-3 px-4 py-5 text-xs text-background/60 md:flex-row md:items-center md:justify-between md:px-6">
          <p>
            © {year} Match&amp;Play · {t('landing.footer.madeWith')}
          </p>
          <ul
            aria-label={t('landing.footer.legalAria')}
            className="flex flex-wrap items-center gap-x-5 gap-y-1"
          >
            <li>
              <Link to={termsHref} className={LINK}>
                {t('landing.footer.legal.terms')}
              </Link>
            </li>
            <li>
              <Link to={privacyHref} className={LINK}>
                {t('landing.footer.legal.privacy')}
              </Link>
            </li>
            {!isNative() && (
              <li>
                <button type="button" onClick={reopenConsent} className={LINK}>
                  {t('consent.manageButton')}
                </button>
              </li>
            )}
          </ul>
        </div>
      </div>
    </footer>
  )
}

interface FooterColumnProps {
  title: string
  links: Array<{ to: string; label: string }>
}

function FooterColumn({ title, links }: FooterColumnProps) {
  return (
    <div>
      <p className="mb-4 font-brutal text-[11px] font-bold uppercase tracking-widest text-background/50">
        {title}
      </p>
      <ul className="space-y-1 text-sm">
        {links.map((link) => {
          // /sessions/new → CreateSessionLink (abre el modal de cooldown si aplica).
          const LinkComponent = link.to === '/sessions/new' ? CreateSessionLink : Link
          return (
            <li key={link.to}>
              <LinkComponent to={link.to} className={LINK}>
                {link.label}
              </LinkComponent>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
